/**
 * **日常账户**的判据 —— 「记账时能选哪些账户」的唯一实现（`DR-066` D1 · `AC-B5.1`）。
 *
 * ## 🔴 为什么单独一个文件
 *
 * 修前 `NewTransactionPage` 只写了一句 `.filter((a) => !a.hidden)`，
 * 而服务端返的是**全量账户** ⇒ 借贷往来、贷款、投资、社保品类全挤在"记一笔"的下拉里，
 * 用户要在十几个"不该出现"的账户里找一个储蓄卡（负责人 2026-10-28 第 2 条）。
 *
 * 🔴 这不是"筛选实现细节"，是**产品口径**：拿往来账户记账会把"借出 / 收回"记成消费，
 * 拿贷款账户记账会把"还款"记成支出 —— 两者都会直接污染唯一支出口径（`DR-005`）。
 * ⇒ 判据必须在**一处**定义（铁律 24），别处**禁止**再写 `type === 'contra'` 这类判断。
 *
 * ## 判据表（`DR-066` D1）
 *
 * | 类型 | 算日常账户 | 为什么 |
 * |---|:--:|---|
 * | `cash` / `debit` / `ewallet` | ✅ | 钱直接进出的地方 |
 * | `credit` | ✅ | 刷卡也是花钱（账单另行核算） |
 * | `internal` + `prepaid` | ✅ | 预付卡是**储值**，花它等于花钱 |
 * | `contra` | ❌ | 往来本金不计收支（`DR-022`） |
 * | `loan` | ❌ | 贷款余额靠还款流程变，不从"贷款里花钱" |
 * | `investment` | ❌ | 投资进出走投资域 |
 * | `internal` + 其余 12 品类 | ❌ | 待缴 / 待摊 / 保险 / 社保是**挂账载体** |
 *
 * ## ⚠️ 刻意**不**看 `hidden`
 *
 * `hidden` 是"不在净资产 / 主列表里占位置"的**展示偏好**（社保账户预置即隐藏，见
 * `account-seeder.ts`），不是"不能用它花钱"。判据只读 `type` + `category` ——
 * 签名里也不收 `hidden`，让"顺手加一条 filter"写不出来。
 * （社保账户已由 `internal` + 非 `prepaid` 品类排除，不需要再按 `hidden` 兜一层。）
 */

import { AccountType, InternalCategory, type AccountView } from '@jiaying/shared';

/** 判据所需的最小投影 —— 只读这两个字段，**刻意不含 `hidden`** */
export type DailyAccountLike = Pick<AccountView, 'type' | 'category'>;

/** 该账户是否属于「日常账户」（`DR-066` D1）。唯一实现。 */
export function isDailyAccount(a: DailyAccountLike): boolean {
  switch (a.type) {
    case AccountType.Cash:
    case AccountType.Debit:
    case AccountType.Ewallet:
    case AccountType.Credit:
      return true;
    case AccountType.Internal:
      // 🔴 只有预付卡（储值）；其余 12 个品类是挂账载体
      return a.category === InternalCategory.Prepaid;
    // `contra` / `loan` / `investment` —— 三类都明确排除
    default:
      return false;
  }
}

/**
 * 过滤出日常账户（**保持传入顺序** —— 顺序由服务端给的 `sort` 决定，本函数不重排）。
 *
 * 泛型保留调用方自己的投影类型（`NewTransactionPage` 传的是 `Option`），
 * 这样收窄之后不必再把对象搬一遍（搬一遍就会有人漏字段）。
 */
export function dailyAccountsOf<T extends DailyAccountLike>(items: readonly T[]): T[] {
  return items.filter(isDailyAccount);
}
